import { notifications } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';
import { pullAllNotice, pullNotice } from '../domain/pullSummary.js';
import { useTRPC } from '../utils/trpc.js';
import { usePullingEnvironments } from './usePullingEnvironments.js';

/**
 * Pull now, for one environment or for all of them, one after another, with the caller's own
 * token. Each environment is its own `environments.pullNow` request, so none outlives
 * `CONNECTOR_TIMEOUT_MS` or the load balancer's idle timeout however many there are; each
 * invalidates the cache on its own (`App.tsx`'s `MutationCache.onSuccess`). A single pull's
 * failure is the global mutation toast; Pull all's are summarised in its one toast.
 *
 * `pulling` names every environment with a `pullNow` in flight anywhere, read off the mutation
 * cache (`usePullingEnvironments`), so every caller agrees however a pull was started;
 * `pullingAll` is this hook's own Pull all.
 */
export const usePull = () => {
    const trpc = useTRPC();
    const pulling = usePullingEnvironments();
    const pullNow = useMutation(trpc.environments.pullNow.mutationOptions());
    // Pull all's per-environment calls: shares `pullNow`'s mutation key so `usePullingEnvironments`
    // still sees it, but summarised by the caller rather than toasting each one, and
    // `skipInvalidate: true` since it runs once per environment — `all` below invalidates once,
    // after every environment has settled, instead of once per environment.
    const pullEach = useMutation({
        ...trpc.environments.pullNow.mutationOptions(),
        meta: { quiet: true, skipInvalidate: true },
    });

    const pull = (environment: string): void =>
        pullNow.mutate({ environment }, { onSuccess: (result) => notifications.show(pullNotice(result)) });

    const all = useMutation({
        mutationFn: async (environments: string[]) => {
            const outcomes: { environment: string; ok: boolean }[] = [];
            for (const environment of environments) {
                try {
                    outcomes.push({ environment, ok: (await pullEach.mutateAsync({ environment })).ok });
                } catch {
                    outcomes.push({ environment, ok: false });
                }
            }
            return outcomes;
        },
        onSuccess: (outcomes) => notifications.show(pullAllNotice(outcomes)),
    });

    return { pull, pullAll: (environments: string[]) => all.mutate(environments), pulling, pullingAll: all.isPending };
};
